<template>
  <div data-testid="shortcuts-menu" class="shortcuts-menu-wrapper">
    <v-list data-testid="shortcuts-menu__list" class="pa-0 d-flex flex-column gap-1">
      <!-- Loading State -->
      <template v-if="loading">
        <v-list-item data-testid="shortcuts-menu__loading" disabled class="py-4 d-flex justify-center">
          <v-list-item-content class="text-center">
            <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
          </v-list-item-content>
        </v-list-item>
      </template>

      <!-- Shortcuts List -->
      <template v-else>
        <v-list-item
          data-testid="shortcuts-menu__item"
          v-for="shortcut in shortcuts"
          :key="shortcut._id || `${shortcut.name}-${shortcut.url}`"
          :to="shortcut.url"
          link
          class="py-1 d-flex align-center shortcut-item"
          @click.stop="handleShortcutClick(shortcut)"
        >
          <v-list-item-content data-testid="shortcuts-menu__item-content" class="py-0">
            <v-list-item-title data-testid="shortcuts-menu__item-title" class="text-truncate">
              {{ shortcut.name }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="shortcut.meta && shortcut.meta.description" class="text-caption text-truncate">
              {{ shortcut.meta.description }}
            </v-list-item-subtitle>
          </v-list-item-content>
          
          <v-progress-circular
            v-if="isRemoving(shortcut)"
            indeterminate
            size="16"
            width="2"
            color="primary"
            class="ml-2"
          />
          <v-icon 
            v-else
            small 
            class="ml-2" 
            color="error" 
            @click.stop.prevent="handleRemove(shortcut)"
          >
            mdi-close-circle
          </v-icon>
        </v-list-item>

        <!-- Empty State -->
        <v-list-item 
          data-testid="shortcuts-menu__empty" 
          v-if="!shortcuts || shortcuts.length === 0" 
          disabled
          class="py-4"
        >
          <v-list-item-content class="text-center">
            <v-list-item-title data-testid="shortcuts-menu__empty-text" class="grey--text text-wrap">
              No shortcuts yet
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

    </v-list>
  </div>
</template>

<script>
export default {
  name: 'ShortcutsMenu',
  props: {
    mini: {
      type: Boolean,
      default: false
    },
    component: {
      type: String,
      default: 'financialclose'
    }
  },
  computed: {
    componentKey() {
      return (this.component || 'financialclose').toLowerCase();
    },
    shortcutsState() {
      return this.$store.getters['shortcuts/componentSummary'](this.componentKey) || {};
    },
    shortcuts() {
      return this.shortcutsState.shortcuts || [];
    },
    loading() {
      return !!this.shortcutsState.loading;
    },
    pinLimit() {
      const limit = this.shortcutsState.pinLimit;
      return typeof limit === 'number' ? limit : 5;
    },
    removingShortcutId() {
      return this.shortcutsState.removingKey || null;
    }
  },
  mounted() {
    this.fetchShortcuts();
  },
  methods: {
    notifySuccess(message) {
      this.$store?.commit?.('sbSuccess', message);
      this.$toast?.success?.(message);
    },

    notifyError(message) {
      this.$store?.commit?.('sbError', message);
      this.$toast?.error?.(message);
    },

    notifyWarning(message) {
      this.$store?.commit?.('sbWarning', message);
      this.$toast?.warning?.(message);
    },

    notifyInfo(message) {
      this.$store?.commit?.('sbInfo', message);
      this.$toast?.info?.(message);
    },

    async fetchShortcuts() {
      const result = await this.$store.dispatch('shortcuts/fetchShortcuts', { component: this.componentKey });
      if (!result?.success && result?.message) {
        this.notifyError(result.message);
      }
    },

    async addShortcut(shortcutData) {
      const result = await this.$store.dispatch('shortcuts/addShortcut', {
        component: this.componentKey,
        shortcut: shortcutData
      });

      const message = result?.message || 'Unable to add shortcut.';

      if (result?.type === 'success') {
        this.notifySuccess(message);
        this.$emit('shortcut-added', shortcutData);
        return true;
      }

      if (result?.type === 'warning') {
        this.notifyWarning(message);
        return false;
      }

      if (result?.type === 'info') {
        this.notifyInfo(message);
        return false;
      }

      this.notifyError(message);
      return false;
    },

    async handleRemove(shortcut) {
      if (!shortcut) {
        this.notifyError('Invalid shortcut.');
        return;
      }

      const result = await this.$store.dispatch('shortcuts/removeShortcut', {
        component: this.componentKey,
        shortcut
      });

      const message = result?.message || 'Failed to remove shortcut.';

      if (result?.type === 'success') {
        this.notifySuccess(message);
        this.$emit('shortcut-removed', shortcut);
        return;
      }

      if (result?.type === 'warning') {
        this.notifyWarning(message);
        return;
      }

      if (result?.type === 'info') {
        this.notifyInfo(message);
        return;
      }

      this.notifyError(message);
    },

    handleShortcutClick(shortcut) {
      this.$root.closeFloatingComponents?.();
      this.$emit('shortcut-click', shortcut);
    },

    isMdi(val) {
      return typeof val === 'string' && /^mdi[\s-]/.test(val);
    },
    
    isImg(val) {
      return typeof val === 'string' && /\.(svg|png|jpe?g|webp|gif)$/i.test(val);
    },

    refresh() {
      return this.fetchShortcuts();
    },

    isRemoving(shortcut) {
      if (!shortcut) return false;
      const id = shortcut._id || `${shortcut.name}-${shortcut.url || ''}`;
      return this.removingShortcutId === id;
    }
  },
  watch: {
    component() {
      this.fetchShortcuts();
    }
  }
};
</script>

<style scoped>
.shortcuts-menu-wrapper {
  min-width: 240px;
  max-width: 320px;
}
</style>
